import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * At most one refetch per view per window, however many frames arrive.
 *
 * **Why this exists.** The `occupancy` frame is published for every accepted movement in the
 * building, and each one used to refetch the occupancy snapshot on every terminal showing it — one
 * request per movement per terminal, with nothing merging them. At a busy door that is the whole
 * building's traffic multiplied by its screens, all of it against the per-address budget every
 * terminal behind the facility's one address shares. TanStack does not merge them either: an
 * invalidation CANCELS a fetch in flight and starts another, and the cancelled request has usually
 * reached the server already.
 *
 * **Leading and trailing, per view.** The first frame refetches at once, so a quiet building is
 * exactly as live as before; frames inside the next {@link REALTIME_COALESCE_WINDOW_MS} are folded
 * into ONE refetch at the end of it, and a steady stream of movements costs a refetch per window
 * rather than one per movement. It never writes a figure — every handler still invalidates and lets
 * the route answer (R397) — it only decides how often the route is asked.
 *
 * **`cancelRefetch: false`, and an edge that finds a fetch running waits for the next one.** A
 * refetch that cancelled a slow one on a slow connection would cancel it again every window and
 * never finish. But an invalidation that lands while an older fetch is running is lost when that
 * fetch succeeds (success clears the stale mark), so that edge keeps its work pending instead of
 * dropping it.
 *
 * **A hidden tab refetches nothing.** Its views are marked stale and refetched once when it is
 * shown again; nobody is reading a screen in a background tab, and its requests count against the
 * same address budget as the gate's.
 *
 * **An invalidation that was never going to refetch passes straight through.** The incident log
 * and a person's record write an audit row whenever they are READ, so a frame from somebody else's
 * terminal only marks them stale (`apply.ts`). Those keys are not remembered for the moment a tab
 * becomes visible, which is what keeps showing a tab from filing an audit row nobody asked for.
 */

/** How long a view's trailing refetch waits after its leading one. */
export const REALTIME_COALESCE_WINDOW_MS = 5000;

/** Whether this document is being looked at, and a way to hear when it starts being. */
export interface VisibilitySource {
  readonly hidden: () => boolean;
  /** Calls the listener each time the document becomes visible; returns the unsubscribe. */
  readonly onVisible: (listener: () => void) => () => void;
}

/** The two things a frame can ask of the cache. */
export interface InvalidationSink {
  /** Refetch the active views under this key, coalesced; mark them stale either way. */
  readonly invalidate: (queryKey: QueryKey) => void;
  /** Mark the views under this key stale and NEVER refetch them on a frame's account. */
  readonly markStale: (queryKey: QueryKey) => void;
}

export interface InvalidationCoalescer extends InvalidationSink {
  /** Cancels every pending edge and stops listening. Nothing refetches after this returns. */
  readonly dispose: () => void;
}

/** The document's own visibility, which is the only source the application uses. */
function documentVisibility(): VisibilitySource {
  return {
    hidden: () => document.visibilityState === 'hidden',
    onVisible: (listener) => {
      const onChange = (): void => {
        if (document.visibilityState === 'visible') {
          listener();
        }
      };
      document.addEventListener('visibilitychange', onChange);
      return () => {
        document.removeEventListener('visibilitychange', onChange);
      };
    },
  };
}

interface ThrottleWindow {
  readonly queryKey: QueryKey;
  /** Whether another refetch was asked for since this window's edge. */
  pending: boolean;
  cancel: () => void;
}

export function createInvalidationCoalescer(
  queries: QueryClient,
  options: {
    readonly schedule: (work: () => void, ms: number) => () => void;
    readonly visibility?: VisibilitySource;
    readonly windowMs?: number;
  },
): InvalidationCoalescer {
  const windowMs = options.windowMs ?? REALTIME_COALESCE_WINDOW_MS;
  const visibility = options.visibility ?? documentVisibility();
  const windows = new Map<string, ThrottleWindow>();
  /** Views a hidden tab was asked to refetch, owed one refetch when it is shown. */
  const owed = new Map<string, QueryKey>();
  let disposed = false;

  const identity = (queryKey: QueryKey): string => JSON.stringify(queryKey);

  const refetch = (queryKey: QueryKey): void => {
    void queries.invalidateQueries({ queryKey }, { cancelRefetch: false });
  };

  /** Refetches now, unless a fetch is already running — then the work is still owed. */
  const edge = (queryKey: QueryKey): boolean => {
    if (queries.isFetching({ queryKey }) > 0) {
      // Marked stale so a mounted view knows, and reported as not done, so the window stays open.
      void queries.invalidateQueries({ queryKey, refetchType: 'none' });
      return false;
    }
    refetch(queryKey);
    return true;
  };

  const open = (queryKey: QueryKey, pending: boolean): void => {
    const key = identity(queryKey);
    const throttle: ThrottleWindow = { queryKey, pending, cancel: () => undefined };
    throttle.cancel = options.schedule(() => {
      windows.delete(key);
      if (disposed || !throttle.pending) {
        return;
      }
      if (visibility.hidden()) {
        owed.set(key, queryKey);
        return;
      }
      // The trailing edge, which opens the next window whether or not it found a fetch running:
      // a steady stream of frames is one refetch per window, never one per frame.
      open(queryKey, !edge(queryKey));
    }, windowMs);
    windows.set(key, throttle);
  };

  const unsubscribe = visibility.onVisible(() => {
    if (disposed) {
      return;
    }
    const due = [...owed.values()];
    owed.clear();
    for (const queryKey of due) {
      refetch(queryKey);
    }
  });

  return {
    invalidate: (queryKey) => {
      if (disposed) {
        return;
      }
      const key = identity(queryKey);
      if (visibility.hidden()) {
        void queries.invalidateQueries({ queryKey, refetchType: 'none' });
        owed.set(key, queryKey);
        return;
      }
      const throttle = windows.get(key);
      if (throttle !== undefined) {
        throttle.pending = true;
        return;
      }
      open(queryKey, !edge(queryKey));
    },
    markStale: (queryKey) => {
      if (disposed) {
        return;
      }
      void queries.invalidateQueries({ queryKey, refetchType: 'none' });
    },
    dispose: () => {
      disposed = true;
      for (const throttle of windows.values()) {
        throttle.cancel();
        if (throttle.pending) {
          // A trailing edge this subscription will never run. Marked stale rather than fetched,
          // so whatever mounts next knows its figures are behind the building and asks again.
          void queries.invalidateQueries({ queryKey: throttle.queryKey, refetchType: 'none' });
        }
      }
      windows.clear();
      owed.clear();
      unsubscribe();
    },
  };
}
